<template>
  <HrPageLayout
    title="Position Structure"
    subtitle="Define reporting lines — who each position reports to in your organization"
    icon="📊"
    :breadcrumb-items="[
      { label: 'System Administration' },
      { label: 'Position & Roles', route: '/position-role-management' },
      { label: 'Position Structure' }
    ]"
  >
    <template #header-actions>
      <router-link to="/position-role-management" class="hr-btn hr-btn-secondary pst-link-btn">
        <i class="bx bx-briefcase"></i>
        Manage positions
      </router-link>
      <button type="button" class="hr-btn hr-btn-primary" :disabled="loading" @click="loadData()">
        <i class="bx bx-refresh"></i>
        {{ loading ? "Loading…" : "Refresh" }}
      </button>
    </template>

    <div class="pst-page">
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-sitemap"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total positions</p>
          <h2 class="hr-metric-value">{{ positions.length }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-crown"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Top level</p>
          <h2 class="hr-metric-value">{{ topLevelCount }}</h2>
            </div>
          <p class="hr-metric-subtext">No reporting line</p>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-git-merge"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Reporting lines</p>
          <h2 class="hr-metric-value">{{ reportingCount }}</h2>
            </div>
          <p class="hr-metric-subtext">Positions with a manager</p>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-layer"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Max depth</p>
          <h2 class="hr-metric-value">{{ maxDepth }}</h2>
            </div>
          <p class="hr-metric-subtext">Levels in hierarchy</p>
        </div>
      </div>

      <div class="hr-chart-card">
        <div class="hr-chart-header pst-toolbar">
          <div class="pst-toolbar-left">
            <div class="pst-search-wrap">
              <i class="bx bx-search-alt search-icon"></i>
              <input
                v-model="searchQuery"
                type="text"
                class="hr-input"
                placeholder="Search position, code, or manager…"
              >
              <button v-if="searchQuery" type="button" class="pst-clear" @click="searchQuery = ''">
                <i class="bx bx-x"></i>
              </button>
            </div>
            <div class="pst-view-toggle">
              <button
                type="button"
                class="pst-view-btn"
                :class="{ active: viewMode === 'list' }"
                @click="viewMode = 'list'"
              >
                <i class="bx bx-list-ul"></i> List
              </button>
              <button
                type="button"
                class="pst-view-btn"
                :class="{ active: viewMode === 'tree' }"
                @click="viewMode = 'tree'"
              >
                <i class="bx bx-git-branch"></i> Hierarchy
              </button>
            </div>
          </div>
          <p class="hr-text-muted hr-mb-0 pst-count">{{ filteredPositions.length }} position(s)</p>
        </div>

        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-wrap">
            <div class="hr-spinner"></div>
            <p>Loading position structure…</p>
          </div>

          <div v-else-if="positions.length === 0" class="hr-empty-state">
            <i class="bx bx-sitemap"></i>
            <h5>No positions yet</h5>
            <p>
              Create positions in
              <router-link to="/position-role-management">Position &amp; Roles</router-link>
              first, then return here to set reporting lines.
            </p>
          </div>

          <div v-else-if="filteredPositions.length === 0" class="hr-empty-state">
            <i class="bx bx-search"></i>
            <h5>No matches</h5>
            <p>Try a different search term or clear the filter.</p>
          </div>

          <!-- List view -->
          <div v-else-if="viewMode === 'list'">
            <div class="pst-hint">
              <i class="bx bx-info-circle"></i>
              Top-level positions have no manager. Use <strong>Reports to</strong> to change reporting lines — changes save immediately.
            </div>
            <div class="hr-table-wrapper">
              <table class="hr-table hr-table-hover pst-table">
                <thead>
                  <tr>
                    <th style="width: 48px;">#</th>
                    <th>Position</th>
                    <th>Code</th>
                    <th>Reports to</th>
                    <th>Roles</th>
                    <th style="min-width: 220px;">Change reports to</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(pos, idx) in filteredSortedPositions"
                    :key="pos.id"
                    :class="{ 'pst-row-root': pos.parentPositionId == null }"
                  >
                    <td class="pst-num">{{ idx + 1 }}</td>
                    <td>
                      <div class="pst-position-cell" :style="{ paddingLeft: getPositionDepth(pos) * 16 + 'px' }">
                        <span v-if="getPositionDepth(pos)" class="pst-indent-icon"><i class="bx bx-subdirectory-right"></i></span>
                        <div>
                          <strong>{{ pos.positionName }}</strong>
                          <span v-if="pos.parentPositionId == null" class="hr-badge hr-badge-purple pst-root-badge">Top level</span>
                          <p v-if="pos.description" class="pst-desc">{{ pos.description }}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span v-if="pos.code" class="hr-badge hr-badge-secondary">{{ pos.code }}</span>
                      <span v-else class="hr-text-muted">—</span>
                    </td>
                    <td>
                      <span v-if="pos.parentPositionName" class="pst-manager">
                        <i class="bx bx-up-arrow-alt"></i> {{ pos.parentPositionName }}
                      </span>
                      <span v-else class="hr-text-muted pst-top-label">— None —</span>
                    </td>
                    <td>
                      <div v-if="pos.roleNames && pos.roleNames.length" class="pst-role-badges">
                        <span v-for="r in pos.roleNames.slice(0, 3)" :key="r" class="hr-badge hr-badge-primary">{{ r }}</span>
                        <span v-if="pos.roleNames.length > 3" class="hr-badge hr-badge-muted">+{{ pos.roleNames.length - 3 }}</span>
                      </div>
                      <span v-else class="hr-text-muted">—</span>
                    </td>
                    <td>
                      <div class="pst-select-wrap">
                        <select
                          v-model="pos._parentId"
                          class="hr-select pst-select"
                          :disabled="savingId === pos.id"
                          @change="saveParent(pos)"
                        >
                          <option :value="null">— None (top level) —</option>
                          <option
                            v-for="p in otherPositions(pos.id)"
                            :key="p.id"
                            :value="p.id"
                          >
                            {{ p.positionName }}{{ p.code ? " (" + p.code + ")" : "" }}
                          </option>
                        </select>
                        <span v-if="savingId === pos.id" class="pst-saving"><span class="pst-mini-spinner"></span></span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Tree view -->
          <div v-else class="pst-tree-panel">
            <div class="pst-hint">
              <i class="bx bx-git-branch"></i>
              Visual hierarchy from top-level positions down. Orphan positions (no parent in tree) appear separately.
            </div>
            <div v-if="filteredTreeRoots.length" class="pst-tree-forest">
              <PositionTreeNode
                v-for="root in filteredTreeRoots"
                :key="root.id"
                :node="root"
                :positions="positions"
                :depth="0"
              />
            </div>
            <div v-if="orphanPositions.length" class="pst-orphans">
              <h4 class="pst-orphans-title"><i class="bx bx-unlink"></i> Unlinked positions</h4>
              <p class="pst-orphans-desc">These positions are not reachable from a top-level root (check reporting assignments).</p>
              <div class="pst-orphan-list">
                <span v-for="o in orphanPositions" :key="o.id" class="hr-badge hr-badge-warning">{{ o.positionName }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import { authUrl, execute } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import PositionTreeNode from "@/components/employeeRegister/PositionTreeNode.vue";

export default {
  name: "PositionStructure",
  components: { HrPageLayout, PositionTreeNode },
  data() {
    return {
      loading: false,
      positions: [],
      savingId: null,
      searchQuery: "",
      viewMode: "list"
    };
  },
  computed: {
    topLevelCount() {
      return this.positions.filter((p) => p.parentPositionId == null).length;
    },
    reportingCount() {
      return this.positions.filter((p) => p.parentPositionId != null).length;
    },
    maxDepth() {
      if (!this.positions.length) return 0;
      let max = 0;
      this.positions.forEach((p) => {
        max = Math.max(max, this.getPositionDepth(p));
      });
      return max + 1;
    },
    filteredPositions() {
      const q = (this.searchQuery || "").trim().toLowerCase();
      if (!q) return this.positions;
      return this.positions.filter((p) => {
        const hay = [
          p.positionName,
          p.code,
          p.description,
          p.parentPositionName,
          ...(p.roleNames || [])
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    },
    sortedPositions() {
      return this.sortHierarchy(this.positions);
    },
    filteredSortedPositions() {
      return this.sortHierarchy(this.filteredPositions);
    },
    treeRoots() {
      return this.positions
        .filter((p) => p.parentPositionId == null)
        .sort((a, b) => (a.positionName || "").localeCompare(b.positionName || ""));
    },
    filteredTreeRoots() {
      const ids = new Set(this.filteredPositions.map((p) => p.id));
      return this.treeRoots.filter((r) => ids.has(r.id) || this.subtreeMatchesSearch(r.id));
    },
    orphanPositions() {
      const reachable = new Set();
      const walk = (parentId) => {
        this.positions
          .filter((p) => p.parentPositionId === parentId)
          .forEach((p) => {
            if (reachable.has(p.id)) return;
            reachable.add(p.id);
            walk(p.id);
          });
      };
      this.treeRoots.forEach((r) => {
        reachable.add(r.id);
        walk(r.id);
      });
      return this.positions.filter((p) => !reachable.has(p.id));
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    sortHierarchy(list) {
      const byParent = {};
      list.forEach((p) => {
        const pid = p.parentPositionId != null ? p.parentPositionId : 0;
        if (!byParent[pid]) byParent[pid] = [];
        byParent[pid].push(p);
      });
      const ordered = [];
      const seen = new Set();
      const push = (parentId) => {
        (byParent[parentId] || [])
          .sort((a, b) => (a.positionName || "").localeCompare(b.positionName || ""))
          .forEach((p) => {
            if (seen.has(p.id)) return;
            seen.add(p.id);
            ordered.push(p);
            push(p.id);
          });
      };
      push(0);
      list.forEach((p) => {
        if (!seen.has(p.id)) ordered.push(p);
      });
      return ordered;
    },
    getPositionDepth(pos) {
      let depth = 0;
      let current = pos;
      const seen = new Set();
      while (current && current.parentPositionId != null && !seen.has(current.id)) {
        seen.add(current.id);
        depth++;
        current = this.positions.find((p) => p.id === current.parentPositionId);
        if (depth > 30) break;
      }
      return depth;
    },
    subtreeMatchesSearch(rootId) {
      const q = (this.searchQuery || "").trim().toLowerCase();
      if (!q) return true;
      const stack = [rootId];
      const seen = new Set();
      while (stack.length) {
        const id = stack.pop();
        if (seen.has(id)) continue;
        seen.add(id);
        const node = this.positions.find((p) => p.id === id);
        if (!node) continue;
        const hay = [node.positionName, node.code, node.description, ...(node.roleNames || [])]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (hay.includes(q)) return true;
        this.positions.filter((p) => p.parentPositionId === id).forEach((c) => stack.push(c.id));
      }
      return false;
    },
    otherPositions(excludeId) {
      return this.positions
        .filter((p) => p.id !== excludeId)
        .sort((a, b) => (a.positionName || "").localeCompare(b.positionName || ""));
    },
    async loadData() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getPositionsWithStructure");
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          const list = res.data.data.positions || [];
          list.forEach((p) => {
            p._parentId = p.parentPositionId != null ? p.parentPositionId : null;
          });
          this.positions = list;
        } else {
          this.positions = [];
        }
      } catch (e) {
        console.error("Error loading position structure:", e);
        this.positions = [];
      } finally {
        this.loading = false;
      }
    },
    async saveParent(pos) {
      this.savingId = pos.id;
      try {
        const data = new FormData();
        data.append("function", "updatePositionParent");
        data.append("positionId", pos.id);
        data.append("parentPositionId", pos._parentId != null ? pos._parentId : "");
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          pos.parentPositionId = pos._parentId;
          pos.parentPositionName =
            pos._parentId != null
              ? (this.positions.find((p) => p.id === pos._parentId) || {}).positionName || ""
              : null;
          this.$toast?.success?.("Reporting structure updated") || alert("Updated.");
        } else {
          this.$toast?.error?.(res?.data?.message || "Update failed") || alert(res?.data?.message || "Update failed");
          pos._parentId = pos.parentPositionId != null ? pos.parentPositionId : null;
        }
      } catch (e) {
        this.$toast?.error?.("Error updating") || alert("Error updating");
        pos._parentId = pos.parentPositionId != null ? pos.parentPositionId : null;
      } finally {
        this.savingId = null;
      }
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.pst-link-btn {
  text-decoration: none;
}

.pst-toolbar {
  flex-wrap: wrap;
}

.pst-toolbar-left {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.pst-search-wrap {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 360px;
}

.pst-search-wrap .hr-input {
  padding-left: 38px;
  padding-right: 36px;
}

.pst-search-wrap .search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}

.pst-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: none;
  color: #64748b;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
}

.pst-view-toggle {
  display: inline-flex;
  padding: 4px;
  background: #f1f5f9;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.pst-view-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, box-shadow 0.15s;
}

.pst-view-btn.active {
  background: #fff;
  color: #4f46e5;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
}

.pst-count {
  font-size: 13px;
}

.hr-loading-wrap {
  text-align: center;
  padding: 3rem;
}

.hr-mb-4 {
  margin-bottom: 1.5rem;
}

.hr-mb-0 {
  margin-bottom: 0;
}

.hr-text-muted {
  color: #94a3b8;
}

.pst-hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 14px;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #f0f9ff, #eef2ff);
  border: 1px solid #e0e7ff;
  border-radius: 12px;
  font-size: 0.88rem;
  color: #475569;
  line-height: 1.45;
}

.pst-hint i {
  color: #6366f1;
  font-size: 1.1rem;
  margin-top: 1px;
  flex-shrink: 0;
}

.pst-table tbody tr.pst-row-root {
  background: linear-gradient(90deg, rgba(99, 102, 241, 0.06) 0%, transparent 100%);
}

.pst-num {
  color: #94a3b8;
  font-weight: 600;
  font-size: 12px;
}

.pst-position-cell {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.pst-indent-icon {
  color: #cbd5e1;
  font-size: 1rem;
  line-height: 1.4;
  flex-shrink: 0;
}

.pst-desc {
  margin: 4px 0 0;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.35;
}

.pst-root-badge {
  margin-left: 8px;
  vertical-align: middle;
}

.pst-manager {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #334155;
  font-weight: 500;
}

.pst-manager i {
  color: #6366f1;
}

.pst-top-label {
  font-style: italic;
}

.pst-role-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.pst-select-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pst-select {
  min-width: 180px;
  font-size: 13px;
  padding: 8px 12px;
}

.pst-saving {
  display: inline-flex;
}

.pst-mini-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #e2e8f0;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: pstSpin 0.65s linear infinite;
}

@keyframes pstSpin {
  to {
    transform: rotate(360deg);
  }
}

.pst-tree-panel {
  padding-top: 4px;
}

.pst-tree-forest {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.pst-orphans {
  margin-top: 24px;
  padding: 16px 18px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 14px;
}

.pst-orphans-title {
  margin: 0 0 6px;
  font-size: 0.95rem;
  color: #92400e;
  display: flex;
  align-items: center;
  gap: 6px;
}

.pst-orphans-desc {
  margin: 0 0 12px;
  font-size: 0.85rem;
  color: #b45309;
}

.pst-orphan-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

@media (max-width: 768px) {
  .pst-toolbar-left {
    width: 100%;
  }

  .pst-search-wrap {
    max-width: none;
    width: 100%;
  }

  .pst-view-toggle {
    width: 100%;
    justify-content: stretch;
  }

  .pst-view-btn {
    flex: 1;
    justify-content: center;
  }
}
</style>
